/** Server-state hooks. Every mutation invalidates the keys it affects. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createHardwareBom,
  createHardwareProject,
  createHealthMetric,
  createHealthReport,
  deleteHardwareBom,
  deleteHardwareProject,
  deleteHealthMetric,
  deleteHealthReport,
  fetchDailyReview,
  fetchDailySummary,
  fetchEntry,
  fetchHardwarePrices,
  fetchHardwareProject,
  fetchHealthReport,
  fetchInboxItem,
  fetchStats,
  fetchV3ResourceDetail,
  listEntries,
  listHardwareBom,
  listHardwareProjects,
  listHealthMetrics,
  listHealthReports,
  listInbox,
  listMessages,
  listV3Inbox,
  listV3Resources,
  markMessagesRead,
  runInboxBatch,
  searchKb,
  submitReviewFeedback,
  updateHardwareBom,
  updateHardwareProject,
  updateHealthMetric,
  updateHealthReport,
  type EntryFilters,
} from './actions'
import {
  entryKeys,
  hardwareKeys,
  healthKeys,
  inboxKeys,
  messageKeys,
  reviewKeys,
  searchKeys,
  statsKey,
  summaryKeys,
  v3Keys,
} from './keys'
import type {
  BatchAction,
  HardwareBomCreateInput,
  HardwareBomUpdateInput,
  HardwareProjectInput,
  HealthMetricInput,
  HealthMetricQuery,
  HealthMetricUpdateInput,
  HealthReportInput,
} from './types'

export function useStats() {
  return useQuery({
    queryKey: statsKey,
    queryFn: fetchStats,
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  })
}

export function useInbox(status: string) {
  return useQuery({
    queryKey: inboxKeys.list(status),
    queryFn: () => listInbox(status),
  })
}

export function useInboxCounts() {
  return useQuery({
    queryKey: inboxKeys.counts(),
    queryFn: () => listInbox('pending_review', 1),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

export function useInboxDetail(id: string | null) {
  return useQuery({
    queryKey: inboxKeys.detail(id ?? ''),
    queryFn: () => fetchInboxItem(id as string),
    enabled: id !== null,
  })
}

export function useInboxBatch() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (args: { ids: string[]; action: BatchAction; dryRun?: boolean }) => runInboxBatch(args),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: inboxKeys.all })
      void client.invalidateQueries({ queryKey: entryKeys.all })
      void client.invalidateQueries({ queryKey: messageKeys.all })
      void client.invalidateQueries({ queryKey: statsKey })
    },
  })
}

export function useEntries(filters: EntryFilters) {
  return useQuery({
    queryKey: entryKeys.list(filters),
    queryFn: () => listEntries(filters),
  })
}

export function useEntry(id: string | null) {
  return useQuery({
    queryKey: entryKeys.detail(id ?? ''),
    queryFn: () => fetchEntry(id as string),
    enabled: id !== null,
  })
}

export function useDailyReview() {
  return useQuery({
    queryKey: reviewKeys.daily(),
    queryFn: fetchDailyReview,
    staleTime: 60_000,
  })
}

export function useDailySummary(date?: string) {
  return useQuery({
    queryKey: summaryKeys.detail(date ?? 'today'),
    queryFn: () => fetchDailySummary(date),
    staleTime: 5 * 60_000,
  })
}

export function useKbSearch(q: string, limit?: number) {
  const query = q.trim()
  return useQuery({
    queryKey: searchKeys.detail(query, limit),
    queryFn: () => searchKb(query, limit),
    enabled: query.length > 0,
    staleTime: 60_000,
  })
}

export function useReviewFeedback() {
  return useMutation({
    mutationFn: (args: { id: string; remembered: boolean }) => submitReviewFeedback(args.id, args.remembered),
  })
}

export function useMessages(unreadOnly: boolean) {
  return useQuery({
    queryKey: messageKeys.list(unreadOnly),
    queryFn: () => listMessages(unreadOnly),
  })
}

export function useMarkMessagesRead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (args: { ids?: string[]; all?: boolean }) => markMessagesRead(args.ids ?? [], args.all ?? false),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: messageKeys.all })
      void client.invalidateQueries({ queryKey: statsKey })
    },
  })
}

// ---------------------------------------------------------------------------
// v3 知识数据：资源列表 / 详情 / 收件箱（只读）
// ---------------------------------------------------------------------------

export function useV3Resources(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: v3Keys.list(page, pageSize),
    queryFn: () => listV3Resources(page, pageSize),
  })
}

export function useV3ResourceDetail(id: string | null) {
  return useQuery({
    queryKey: v3Keys.detail(id ?? ''),
    queryFn: () => fetchV3ResourceDetail(id as string),
    enabled: id !== null,
  })
}

export function useV3Inbox(status = 'pending', page = 1, pageSize = 20) {
  return useQuery({
    queryKey: v3Keys.inbox(status, page, pageSize),
    queryFn: () => listV3Inbox(status, page, pageSize),
  })
}

// ---------------------------------------------------------------------------
// 硬件：项目 / BOM / 价格基线
// ---------------------------------------------------------------------------

export function useHardwareProjects() {
  return useQuery({
    queryKey: hardwareKeys.projects(),
    queryFn: listHardwareProjects,
  })
}

export function useHardwareProject(id: string | null) {
  return useQuery({
    queryKey: hardwareKeys.project(id ?? ''),
    queryFn: () => fetchHardwareProject(id as string),
    enabled: id !== null,
  })
}

export function useHardwareBom(projectId: string | null) {
  return useQuery({
    queryKey: hardwareKeys.bom(projectId ?? ''),
    queryFn: () => listHardwareBom(projectId as string),
    enabled: projectId !== null,
  })
}

export function useHardwarePrices() {
  return useQuery({
    queryKey: hardwareKeys.prices(),
    queryFn: fetchHardwarePrices,
    staleTime: 60_000,
  })
}

export function useCreateHardwareProject() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: HardwareProjectInput) => createHardwareProject(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hardwareKeys.all })
    },
  })
}

export function useUpdateHardwareProject() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (args: { id: string; input: Partial<HardwareProjectInput> }) =>
      updateHardwareProject(args.id, args.input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hardwareKeys.all })
    },
  })
}

export function useDeleteHardwareProject() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteHardwareProject(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hardwareKeys.all })
    },
  })
}

export function useCreateHardwareBom() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: HardwareBomCreateInput) => createHardwareBom(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hardwareKeys.all })
    },
  })
}

export function useUpdateHardwareBom() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (args: { id: string; input: HardwareBomUpdateInput }) =>
      updateHardwareBom(args.id, args.input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hardwareKeys.all })
    },
  })
}

export function useDeleteHardwareBom() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteHardwareBom(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hardwareKeys.all })
    },
  })
}

// ---------------------------------------------------------------------------
// 健康：指标 / 体检报告
// ---------------------------------------------------------------------------

export function useHealthMetrics(query: HealthMetricQuery = {}) {
  return useQuery({
    queryKey: healthKeys.metrics(query),
    queryFn: () => listHealthMetrics(query),
  })
}

export function useHealthReports() {
  return useQuery({
    queryKey: healthKeys.reports(),
    queryFn: listHealthReports,
  })
}

export function useHealthReport(id: string | null) {
  return useQuery({
    queryKey: healthKeys.report(id ?? ''),
    queryFn: () => fetchHealthReport(id as string),
    enabled: id !== null,
  })
}

export function useCreateHealthMetric() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: HealthMetricInput) => createHealthMetric(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: healthKeys.all })
    },
  })
}

export function useUpdateHealthMetric() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (args: {
      key: { date: string; metric: string }
      input: HealthMetricUpdateInput
    }) => updateHealthMetric(args.key, args.input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: healthKeys.all })
    },
  })
}

export function useDeleteHealthMetric() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (key: { date: string; metric: string }) => deleteHealthMetric(key),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: healthKeys.all })
    },
  })
}

export function useCreateHealthReport() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: HealthReportInput) => createHealthReport(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: healthKeys.all })
    },
  })
}

export function useUpdateHealthReport() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (args: { id: string; input: Partial<HealthReportInput> }) =>
      updateHealthReport(args.id, args.input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: healthKeys.all })
    },
  })
}

export function useDeleteHealthReport() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteHealthReport(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: healthKeys.all })
    },
  })
}
